import GlobalTable from '@/components/probaworkflows_base_components/GlobalTable/GlobalTable.vue';
import {
  LIST_DATA_POINTS_URL,
  get_headers,
  get_project_id
} from '@/utils/constants/constants.js';
import alert_mixin from '@/utils/mixins/alert_mixin';
import compliance_rules_mixins from '@/utils/mixins/compliance_rules_mixins';
import formatting_mixins from '@/utils/mixins/formatting_mixins';
import AssignComplianceRules from '@/views/probaworkflows_base/management/compliance_management/assign_compliance_rules/AssignComplianceRules.vue';
import GroupComplianceRules from '@/views/probaworkflows_base/management/compliance_management/group_compliance_rules/GroupComplianceRules.vue';
import axios from "axios";
import 'datatables.net';
import 'datatables.net-dt/css/dataTables.dataTables.css';
import $ from 'jquery';

// Dropdown options constants
const FILE_AGE_OPTIONS = [
  { value: '1', label: '1 day (Daily)' },
  { value: '7', label: '7 days (Weekly)' },
  { value: '14', label: '14 days (Bi-weekly)' },
  { value: '30', label: '30 days (Monthly)' },
  { value: '60', label: '60 days (Bi-monthly)' },
  { value: '90', label: '90 days (Quarterly)' },
  { value: '180', label: '180 days (Semi-annually)' },
  { value: '365', label: '365 days (Annually)' },
  { value: 'custom', label: 'Custom' }
];

const GRACE_PERIOD_OPTIONS = [
  { value: '0', label: 'No grace period' },
  { value: '1', label: '1 day' },
  { value: '7', label: '7 days (1 week)' },
  { value: '14', label: '14 days (2 weeks)' },
  { value: '30', label: '30 days (1 month)' },
  { value: '60', label: '60 days (2 months)' },
  { value: '90', label: '90 days (3 months)' },
  { value: 'custom', label: 'Custom' }
];

const FREQUENCY_OPTIONS = [
  { value: 'ONCE', label: 'Once' },
  { value: 'DAILY', label: 'Daily' },
  { value: 'WEEKLY', label: 'Weekly' },
  { value: 'MONTHLY', label: 'Monthly' },
  { value: 'QUARTERLY', label: 'Quarterly' },
  { value: 'ANNUALLY', label: 'Annually' }
];

export default {
  name: 'ComplianceRules',
  mixins: [alert_mixin , formatting_mixins, compliance_rules_mixins],
  components: {
    GlobalTable,
    AssignComplianceRules,
    GroupComplianceRules
  },
  data: () => ({
    tabs: ['Add Rules', 'Import Rules'], // Removed 'Group Rules' to substitute as a d-none
    active_tab: 'Add Rules',
    headers: get_headers(),
    project_id: get_project_id(),
    entity_type_id: sessionStorage.getItem("entity_type_id"),
    table_loading: false,
    loading: false,
    archiving_rules: new Set(),
    unarchiving_rules: new Set(),
    show_archived: false,
    errors: {},
    archive_modal: {
      rule: null,
      reason: '',
      action: '',
      loading: false
    },
    
    // Assign constants to data properties for template access
    file_age_options: FILE_AGE_OPTIONS,
    grace_period_options: GRACE_PERIOD_OPTIONS,
    frequency_options: FREQUENCY_OPTIONS,

    new_rule: {
      name: '',
      description: '',
      data_point_id: null,
      frequency: '',
      is_active: false,
      project_id: get_project_id(),
      age_selection: '',
      custom_age_days: null,
      grace_period_selection: '',
      custom_grace_period_days: null,
      notify_admin_ids: [],
    },

    admin_options: [],
    edit_rule: {
      id: null,
      name: '',
      description: '',
      data_point: null,
      frequency: '',
      validation_logic: '{}',
      enforcement_actions: '{}',
      is_active: false,
      age_selection: '',
      custom_age_days: null,
      grace_period_selection: '',
      custom_grace_period_days: null
    },
    data_points_loading: true,
    data_points: [],
    table_headers: ['Rule Name', 'Description', 'Data Point', 'Frequency', 'Status'],
    table_data: [],
    raw_rules_data: [],
    table_safe_headers: [],
    selected_rule_name: '',
    rule_history_data: [],
    entity_types_loading: false,
    entity_types: [],
    assignments_loading: false,
    rule_assignments: [],
    assignment_table_headers: ['Entity Type', 'Compliance Rule', 'Required', 'Status'],
    assignment_table_data: [],
    conditional_logic_placeholder: '{"condition": "entity.field === \'value\'"}',
  }),
  
  mounted() {
    this.fetch_data_points();
    this.fetch_compliance_rules();
    this.fetch_entity_types();
    this.fetch_admin_options();
  },

  computed: {
    // Get the actual age value to send to backend
    computed_allowed_age() {
      if (this.new_rule.age_selection === 'custom') {
        return this.new_rule.custom_age_days;
      } else if (this.new_rule.age_selection) {
        return parseInt(this.new_rule.age_selection, 10);
      }
      return null;
    },

    computed_edit_allowed_age() {
      if (this.edit_rule.age_selection === 'custom') {
        return this.edit_rule.custom_age_days;
      } else if (this.edit_rule.age_selection) {
        return parseInt(this.edit_rule.age_selection, 10);
      }
      return null;
    },

    // Get the actual grace period value to send to backend
    computed_grace_period_days() {
      if (this.new_rule.grace_period_selection === 'custom') {
        return this.new_rule.custom_grace_period_days || 0;
      } else if (this.new_rule.grace_period_selection !== '') {
        return parseInt(this.new_rule.grace_period_selection, 10);
      }
      return 0;
    },

    computed_edit_grace_period_days() {
      if (this.edit_rule.grace_period_selection === 'custom') {
        return this.edit_rule.custom_grace_period_days || 0;
      } else if (this.edit_rule.grace_period_selection !== '') {
        return parseInt(this.edit_rule.grace_period_selection, 10);
      }
      return 0;
    }
  },

  watch: {
    table_data: {
      handler(new_val) {
        if (new_val && new_val.length > 0) {
          this.$nextTick(() => {
            this.init_data_table();
          });
        }
      },
      immediate: true, // This will run the handler immediately with the current value
      deep: true // Watch for changes inside the array
    }
  },

  methods: {
    set_active_tab(tab) {
      // Destroy any existing DataTable before switching
      const table = $('#compliance_rules_table');
      if ($.fn.DataTable.isDataTable(table)) {
        table.DataTable().destroy(true);
      }
      this.active_tab = tab;
    },


    init_data_table() {
      // Check if table exists and destroy properly
      if (this.dataTable) {
          this.dataTable.destroy();
      }
      
      this.dataTable = $('#compliance_rules_table').DataTable({
          responsive: true,
          emptyTable: "No data available in table",
          language: {
              info: "",
              lengthMenu: "_MENU_&nbsp;&nbsp;Entries per page"
          }
      });
    },

    async fetch_data_points() {
      this.data_points_loading = true;
      try {
        const response = await axios.get(LIST_DATA_POINTS_URL, { 
          params: { project_id: this.project_id },
          headers: this.headers 
        });       
        // Extract the data array from the response
        const dataArray = response.data.data || [];
        // Transform data to match expected structure
        this.data_points = dataArray.map(item => ({
          id: item.id,
          name: item.data_point_name || `Data Point ${item.id}`,
        }));

      } catch (error) {
        this.show_error("Error loading data points" || error.message);
      } finally {
        this.data_points_loading = false;
      }
    },

    get_data_point_name(id) {
      const point = this.data_points.find(p => p.id === id);
      return point ? point.name : 'N/A';
    },

    format_frequency(freq) {
      const map = new Map([
        ['ONCE', 'Once'], ['DAILY', 'Daily'],['WEEKLY', 'Weekly'],
        ['MONTHLY', 'Monthly'],['QUARTERLY', 'Quarterly'], ['ANNUALLY', 'Annually']
      ]);
      return map.get(freq) || freq;
    },

    // Updated helper method using the constants
    get_preset_values(option_type) {
      var options;
      
      switch (option_type) {
        case 'file_age':
          options = FILE_AGE_OPTIONS;
          break;
        case 'grace_period':
          options = GRACE_PERIOD_OPTIONS;
          break;
        case 'frequency':
          options = FREQUENCY_OPTIONS;
          break;
        default:
          return [];
      }
      
      if (!options || !Array.isArray(options)) {
        return [];
      }
      
      return options
        .filter(option => option && typeof option === 'object' && option.value !== 'custom')
        .map(option => {
          const parsed = parseInt(option.value, 10);
          return isNaN(parsed) ? 0 : parsed;
        });
    },

    validate_rule() {
      this.errors = {};
      let is_valid = true;

      if (!this.new_rule.name) {
        this.errors.name = "Rule name is required";
        is_valid = false;
      }

      if (!this.new_rule.description) {
        this.errors.description = "Description is required";
        is_valid = false;
      }

      if (!this.new_rule.frequency) {
        this.errors.frequency = "Frequency is required";
        is_valid = false;
      }

      // Validate age selection
      if (!this.new_rule.age_selection) {
        this.errors.allowed_age = "Please select the maximum file age";
        is_valid = false;
      } else if (this.new_rule.age_selection === 'custom') {
        if (!this.new_rule.custom_age_days || this.new_rule.custom_age_days <= 0) {
          this.errors.allowed_age = "Custom age must be greater than 0";
          is_valid = false;
        } else if (this.new_rule.custom_age_days > 3650) {
          this.errors.allowed_age = "Maximum file age cannot exceed 3650 days (about 10 years)";
          is_valid = false;
        }
      }

      // Validate grace period selection
      if (!this.new_rule.grace_period_selection) {
        this.errors.grace_period_days = "Please select a grace period";
        is_valid = false;
      } else if (this.new_rule.grace_period_selection === 'custom') {
        if (this.new_rule.custom_grace_period_days === null || this.new_rule.custom_grace_period_days < 0) {
          this.errors.grace_period_days = "Custom grace period must be 0 or greater";
          is_valid = false;
        } else if (this.new_rule.custom_grace_period_days > 365) {
          this.errors.grace_period_days = "Grace period cannot exceed 365 days (1 year)";
          is_valid = false;
        }
      }

      return is_valid;
    },

    reset_rule_form() {
      this.new_rule = {
        name: '',
        description: '',
        data_point_id: null,
        frequency: '',
        is_active: false,
        project_id: get_project_id(),
        age_selection: '',
        custom_age_days: null,
        grace_period_selection: '',
        custom_grace_period_days: null,
        notify_admin_ids: [],
      };
      this.errors = {};
    },

    toggle_archived() {
      this.show_archived = !this.show_archived;
      this.fetch_compliance_rules();
    },


    validate_edit_rule() {
      this.errors = {};
      let is_valid = true;

      if (!this.edit_rule.name) {
        this.errors.name = "Rule name is required";
        is_valid = false;
      } else if (this.edit_rule.name.length > 50) {
        this.errors.name = "Name must be 50 characters or less";
        is_valid = false;
      }

      if (!this.edit_rule.description) {
        this.errors.description = "Description is required";
        is_valid = false;
      } else if (this.edit_rule.description.length > 255) {
        this.errors.description = "Description must be 255 characters or less";
        is_valid = false;
      }

      if (!this.edit_rule.frequency) {
        this.errors.frequency = "Frequency is required";
        is_valid = false;
      }

      // Validate age selection for edit
      if (!this.edit_rule.age_selection) {
        this.errors.allowed_age = "Please select the maximum file age";
        is_valid = false;
      } else if (this.edit_rule.age_selection === 'custom') {
        if (!this.edit_rule.custom_age_days || this.edit_rule.custom_age_days <= 0) {
          this.errors.allowed_age = "Custom age must be greater than 0";
          is_valid = false;
        } else if (this.edit_rule.custom_age_days > 3650) {
          this.errors.allowed_age = "Maximum file age cannot exceed 3650 days";
          is_valid = false;
        }
      }

      // Validate grace period selection for edit
      if (!this.edit_rule.grace_period_selection) {
        this.errors.grace_period_days = "Please select a grace period";
        is_valid = false;
      } else if (this.edit_rule.grace_period_selection === 'custom') {
        if (this.edit_rule.custom_grace_period_days === null || this.edit_rule.custom_grace_period_days < 0) {
          this.errors.grace_period_days = "Custom grace period must be 0 or greater";
          is_valid = false;
        } else if (this.edit_rule.custom_grace_period_days > 365) {
          this.errors.grace_period_days = "Grace period cannot exceed 365 days";
          is_valid = false;
        }
      }

      try {
        JSON.parse(this.edit_rule.enforcement_actions);
      } catch (e) {
        this.errors.enforcement_actions = "Invalid JSON for enforcement actions";
        is_valid = false;
      }

      return is_valid;
    },

    view_rule_details(version) {
      this.edit_rule = {
        ...version,
        data_point_id: version.data_point,
        validation_logic: JSON.stringify(version.validation_logic, null, 2),
        enforcement_actions: JSON.stringify(version.enforcement_actions, null, 2)
      };
    },


    setup_archive_modal(rule, action) {
      this.archive_modal.rule = rule;
      this.archive_modal.action = action;
      this.archive_modal.reason = '';
      this.archive_modal.loading = false;
    },

    async handle_archive_action() {
      if (!this.archive_modal.reason.trim()) {
        this.show_error("Please provide a reason");
        return;
      }

      this.archive_modal.loading = true;

      try {
        if (this.archive_modal.action === 'archive') {
          await this.archive_rule(this.archive_modal.rule, this.archive_modal.reason);
        } else {
          await this.unarchive_rule(this.archive_modal.rule, this.archive_modal.reason);
        }
        this.close_archive_modal();
      } catch (error) {
        // Error already handled in individual methods
      } finally {
        this.archive_modal.loading = false;
      }
    },

    toggle_admin(admin_id) {
      const idx = this.new_rule.notify_admin_ids.indexOf(admin_id);
      if (idx === -1) {
        this.new_rule.notify_admin_ids.push(admin_id);
      } else {
        this.new_rule.notify_admin_ids.splice(idx, 1);
      }
    },

    get_admin_name(id) {
      const admin = this.admin_options.find(a => a.id === id);
      if (!admin) return `User #${id}`;
      return `${admin.first_name || ''} ${admin.last_name || ''}`.trim() || admin.email;
    },

    get_raw_data(id) {
      const rule = this.raw_rules_data.find(r => r.id === id);
      return rule?._rawData || rule;
    },
  },
  beforeDestroy() {
        if (this.dataTable) {
            this.dataTable.destroy();
        }
    }
};